import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Represents a pinned quick link item
export interface QuickLinkItem {
  id: string;           // Unique identifier (internalName from NavigationItem)
  name: string;         // Display name
  href: string;         // URL to navigate to
  iconName: string;     // Icon identifier for serialization
  order: number;        // Display order (0-4 for 5 max items)
}

export interface QuickLinksState {
  pinnedItems: QuickLinkItem[];
  maxItems: number;
}

const STORAGE_KEY = 'quickLinks';
const MAX_QUICK_LINKS = 5;

// Load from localStorage
const loadFromStorage = (): QuickLinkItem[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate the structure
      if (Array.isArray(parsed)) {
        return parsed.slice(0, MAX_QUICK_LINKS);
      }
    }
  } catch (e) {
    console.error('Failed to load quick links from storage:', e);
  }
  return [];
};

// Save to localStorage
const saveToStorage = (items: QuickLinkItem[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    console.error('Failed to save quick links to storage:', e);
  }
};

const initialState: QuickLinksState = {
  pinnedItems: loadFromStorage(),
  maxItems: MAX_QUICK_LINKS,
};

const quickLinksSlice = createSlice({
  name: 'quickLinks',
  initialState,
  reducers: {
    // Add a new pinned item
    pinItem: (state, action: PayloadAction<Omit<QuickLinkItem, 'order'>>) => {
      // Check if already pinned
      const existingIndex = state.pinnedItems.findIndex(
        (item) => item.id === action.payload.id
      );

      if (existingIndex !== -1) {
        // Item already pinned, don't add again
        return;
      }

      // Check if at max capacity
      if (state.pinnedItems.length >= state.maxItems) {
        // Don't add if at max - user should unpin something first
        return;
      }

      // Add new item with next order number
      const newItem: QuickLinkItem = {
        ...action.payload,
        order: state.pinnedItems.length,
      };

      state.pinnedItems.push(newItem);
      saveToStorage(state.pinnedItems);
    },

    // Remove a pinned item
    unpinItem: (state, action: PayloadAction<string>) => {
      const itemId = action.payload;
      const index = state.pinnedItems.findIndex((item) => item.id === itemId);

      if (index !== -1) {
        state.pinnedItems.splice(index, 1);

        // Reorder remaining items
        state.pinnedItems.forEach((item, idx) => {
          item.order = idx;
        });

        saveToStorage(state.pinnedItems);
      }
    },

    // Reorder items (drag and drop)
    reorderItems: (
      state,
      action: PayloadAction<{ fromIndex: number; toIndex: number }>
    ) => {
      const { fromIndex, toIndex } = action.payload;

      if (
        fromIndex < 0 ||
        fromIndex >= state.pinnedItems.length ||
        toIndex < 0 ||
        toIndex >= state.pinnedItems.length
      ) {
        return;
      }

      // Remove item from old position
      const [movedItem] = state.pinnedItems.splice(fromIndex, 1);

      // Insert at new position
      state.pinnedItems.splice(toIndex, 0, movedItem);

      // Update order numbers
      state.pinnedItems.forEach((item, idx) => {
        item.order = idx;
      });

      saveToStorage(state.pinnedItems);
    },

    // Clear all pinned items
    clearAllPinnedItems: (state) => {
      state.pinnedItems = [];
      saveToStorage(state.pinnedItems);
    },

    // Set items directly (useful for initial load or sync)
    setPinnedItems: (state, action: PayloadAction<QuickLinkItem[]>) => {
      state.pinnedItems = action.payload.slice(0, state.maxItems);
      saveToStorage(state.pinnedItems);
    },
  },
});

export const {
  pinItem,
  unpinItem,
  reorderItems,
  clearAllPinnedItems,
  setPinnedItems,
} = quickLinksSlice.actions;

export default quickLinksSlice.reducer;

// Selectors
export const selectPinnedItems = (state: { quickLinks: QuickLinksState }) =>
  state.quickLinks.pinnedItems;

export const selectIsPinned = (itemId: string) => (state: { quickLinks: QuickLinksState }) =>
  state.quickLinks.pinnedItems.some((item) => item.id === itemId);

export const selectCanPinMore = (state: { quickLinks: QuickLinksState }) =>
  state.quickLinks.pinnedItems.length < state.quickLinks.maxItems;

export const selectPinnedCount = (state: { quickLinks: QuickLinksState }) =>
  state.quickLinks.pinnedItems.length;

export const selectMaxItems = (state: { quickLinks: QuickLinksState }) =>
  state.quickLinks.maxItems;
